import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Segmented,
  Select,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { listSites } from "../../../services/client";
import type {
  PaginatedResponseSiteReadable,
  PartnerOutput,
  SiteReadable,
} from "../../../services/client/types.gen";
import { SiteCard } from "./SiteCard";
import {
  phaseRotationBoardColumns,
  validationBoardColumns,
  type BoardSite,
  type SitesView,
} from "./boardColumns";
import { adminAuthHeaders } from "../adminAuthHeaders";
import { listSiteIoConfigurations } from "../siteIoConfigurations";
import { useOpenSiteAsPartner } from "../useOpenSiteAsPartner";
import {
  filterBoardSites,
  PARTNER_SITE_PARTNER_IDS,
  SITE_STATUSES,
  type SiteFilters,
  type SiteStatus,
} from "./siteFilters";
import { siteStatusTag } from "../../util-components/siteStatusTag";

const { Text } = Typography;

type SitesBoardProps = {
  partners?: PartnerOutput[];
};

const extractErrorMessage = (error: unknown): string => {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Please try again.";
};

const listAllSites = async (): Promise<SiteReadable[]> => {
  const sites: SiteReadable[] = [];
  let after: string | undefined = undefined;
  do {
    const page = await listSites({
      throwOnError: true,
      headers: adminAuthHeaders(),
      query: { page_size: 100, after },
    });
    const body: PaginatedResponseSiteReadable = page.data;
    sites.push(...body.data);
    after = body.has_next_page ? (body.end_cursor ?? undefined) : undefined;
  } while (after);
  return sites;
};

export const SitesBoard: React.FC<SitesBoardProps> = ({ partners }) => {
  const {
    data: boardSites,
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-sites-board"],
    retry: false,
    queryFn: async (): Promise<BoardSite[]> => {
      const [sites, ioConfigurations] = await Promise.all([
        listAllSites(),
        listSiteIoConfigurations(),
      ]);
      const modeBySiteId = Object.fromEntries(
        ioConfigurations.map((configuration) => [
          configuration.site_id,
          configuration.phase_permutation_mode ?? "worst_case",
        ]),
      );
      return sites.map((site) => ({
        site,
        phasePermutationMode: modeBySiteId[site.site_id] ?? "worst_case",
      }));
    },
  });

  const openSiteAsPartner = useOpenSiteAsPartner();

  const partnerNameById = useMemo(
    () =>
      Object.fromEntries(
        (partners ?? []).map((partner) => [partner.partner_id, partner.name]),
      ),
    [partners],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const view: SitesView =
    searchParams.get("view") === "phase_rotation"
      ? "phase_rotation"
      : "validation";
  const updateParams = (update: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams);
    update(params);
    setSearchParams(params, { replace: true });
  };
  const setView = (next: SitesView) =>
    updateParams((params) => params.set("view", next));

  const filters: SiteFilters = {
    search: searchParams.get("q") ?? "",
    partnerIds: searchParams.getAll("partner"),
    statuses: SITE_STATUSES.filter((status) =>
      searchParams.getAll("status").includes(status),
    ),
    hidePartnerSites: searchParams.get("hide_partner_sites") === "true",
  };
  const setSearch = (search: string) =>
    updateParams((params) =>
      search ? params.set("q", search) : params.delete("q"),
    );
  const setPartnerIds = (partnerIds: string[]) =>
    updateParams((params) => {
      params.delete("partner");
      partnerIds.forEach((partnerId) => params.append("partner", partnerId));
    });
  const setStatuses = (statuses: SiteStatus[]) =>
    updateParams((params) => {
      params.delete("status");
      statuses.forEach((status) => params.append("status", status));
    });
  const setHidePartnerSites = (hide: boolean) =>
    updateParams((params) =>
      hide
        ? params.set("hide_partner_sites", "true")
        : params.delete("hide_partner_sites"),
    );

  const filteredSites = filterBoardSites(
    boardSites ?? [],
    filters,
    partnerNameById,
  );

  const columns =
    view === "validation"
      ? validationBoardColumns(filteredSites)
      : phaseRotationBoardColumns(filteredSites);

  const partnerOptions = (partners ?? [])
    .map((partner) => ({
      value: partner.partner_id,
      label: `${partner.name} (${partner.partner_id})`,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load sites"
        description={extractErrorMessage(error)}
      />
    );
  }

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Text type="secondary" className="max-w-2xl">
          Every site across all partners, grouped by validation status or by
          phase rotation compatibility (the IO configuration&apos;s
          phase_permutation_mode combined with whether every asset declares a
          phase rotation).
        </Text>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<SitesView>
            options={[
              { label: "By validation", value: "validation" },
              { label: "By phase rotation", value: "phase_rotation" },
            ]}
            value={view}
            onChange={setView}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input.Search
          allowClear
          placeholder="Search by name, site ID or partner"
          className="w-80"
          value={filters.search}
          onChange={(event) => setSearch(event.target.value)}
          onSearch={setSearch}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by partner"
          className="min-w-[16rem] max-w-xl"
          options={partnerOptions}
          optionFilterProp="label"
          value={filters.partnerIds}
          onChange={setPartnerIds}
        />
        <Select<SiteStatus[]>
          mode="multiple"
          allowClear
          placeholder="Filter by status"
          className="min-w-[12rem]"
          options={SITE_STATUSES.map((status) => ({
            label: siteStatusTag[status].title,
            value: status,
          }))}
          value={filters.statuses}
          onChange={setStatuses}
        />
        <Tooltip
          title={`Hides sites of partners ${PARTNER_SITE_PARTNER_IDS.join(", ")}`}
        >
          <Checkbox
            checked={filters.hidePartnerSites}
            onChange={(event) => setHidePartnerSites(event.target.checked)}
          >
            Hide partner sites
          </Checkbox>
        </Tooltip>
        <Text type="secondary" className="tabular-nums">
          {filteredSites.length} of {boardSites.length} sites
        </Text>
      </div>

      <div
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
          view === "phase_rotation" ? "xl:grid-cols-4" : ""
        }`}
      >
        {columns.map((column) => (
          <div
            key={column.key}
            className="flex min-h-[10rem] flex-col rounded-lg bg-gray-100 p-3"
          >
            <Tooltip title={column.hint}>
              <div className="mb-3 flex items-baseline justify-between gap-2">
                <Text strong className="text-base break-all">
                  {column.title}
                </Text>
                <Text type="secondary" className="tabular-nums">
                  {column.sites.length}
                </Text>
              </div>
            </Tooltip>

            {column.sites.length === 0 ? (
              <div className="flex flex-1 items-center justify-center">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="None"
                  className="!my-0"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {column.sites.map((boardSite) => (
                  <SiteCard
                    key={boardSite.site.site_id}
                    boardSite={boardSite}
                    partnerName={partnerNameById[boardSite.site.partner_id]}
                    onClick={() =>
                      openSiteAsPartner.mutate({
                        partnerId: boardSite.site.partner_id,
                        siteId: boardSite.site.site_id,
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
